import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Lock } from 'lucide-react';
import type { NguoiDung } from '../../server/kieu';
import { api, thongDiepLoi } from '../lib/api';
import { KHOA } from '../lib/du-lieu';

// Tên miền chứa slug brand (vd gorganic.…) → trang đăng nhập mang tên brand (API công khai, không cần phiên).
// Chỉ là giao diện: quyền vẫn theo tài khoản (server/pham-vi.ts).
function useThuongHieuTrang() {
  return useQuery({
    queryKey: ['thuong-hieu-trang'],
    queryFn: () => api.get<{ brand: string | null; ten: string | null }>('thuong-hieu').catch(() => ({ brand: null, ten: null })),
    staleTime: Infinity,
  }).data;
}

export function TrangDangNhap() {
  const th = useThuongHieuTrang();
  const ten = th?.ten || 'VTask';
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [mat_khau, setMatKhau] = useState('');
  const [loi, setLoi] = useState('');
  const [gui, setGui] = useState(false);

  const vao = async (e: React.FormEvent) => {
    e.preventDefault();
    setGui(true);
    setLoi('');
    try {
      const r = await api.post<{ nguoi: NguoiDung }>('dang-nhap', { email, mat_khau });
      qc.setQueryData(KHOA.toi, r.nguoi);
    } catch (e2) {
      setLoi(thongDiepLoi(e2));
    } finally {
      setGui(false);
    }
  };

  return (
    <div className="grid min-h-full lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden bg-[#1b1b1e] p-12 text-white lg:flex lg:flex-col">
        <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#f6821f] opacity-20 blur-3xl" />
        <div className="absolute -bottom-48 -left-24 h-[420px] w-[420px] rounded-full bg-[#0051c3] opacity-25 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#f6821f]">
            <svg viewBox="0 0 32 32" width="22" height="22" aria-hidden><path d="M8 16.5l5 5L24 10.5" stroke="white" strokeWidth="3.4" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </span>
          <span className="text-[18px] font-bold tracking-tight">{ten}</span>
        </div>
        <div className="relative mt-auto max-w-md">
          {th?.brand ? (
            <>
              <h2 className="text-[34px] leading-tight font-semibold tracking-tight">Không gian làm việc của {ten}.</h2>
              <p className="mt-4 text-[15px] leading-relaxed text-white/70">Theo dõi tiến độ, nhận việc và trao đổi ngay trên từng việc của đội.</p>
            </>
          ) : (
            <>
              <h2 className="text-[34px] leading-tight font-semibold tracking-tight">Mọi việc — người và AI — ở một chỗ.</h2>
              <p className="mt-4 text-[15px] leading-relaxed text-white/70">
                Theo dõi tiến độ, giao việc cho nhân sự hoặc AI, trao đổi ngay trên từng việc. Dữ liệu nằm trên GitHub Issues — công cụ AI nào cũng đọc được.
              </p>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center justify-center bg-mat p-6">
        <form onSubmit={vao} className="w-full max-w-sm">
          <div className="mb-8 lg:hidden"><span className="text-[20px] font-bold">{ten}</span></div>
          <h1 className="text-[22px] font-semibold tracking-tight">Đăng nhập</h1>
          <p className="mt-1 mb-6 text-[13px] text-chu-phu">Dùng tài khoản quản trị viên đã cấp cho bạn.</p>
          <label className="block">
            <span className="text-[13px] font-medium">Email</span>
            <input className="o-nhap mt-1 h-10" type="text" inputMode="email" autoComplete="username" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ban@congty.vn" />
          </label>
          <label className="mt-4 block">
            <span className="text-[13px] font-medium">Mật khẩu</span>
            <input className="o-nhap mt-1 h-10" type="password" autoComplete="current-password" required value={mat_khau} onChange={(e) => setMatKhau(e.target.value)} />
          </label>
          {loi && <div role="alert" className="mt-4 rounded-md border border-do/30 bg-do-nhat px-3 py-2 text-[13px] text-do">{loi}</div>}
          <button className="nut-chinh mt-6 h-10 w-full text-[14px]" disabled={gui}>
            {gui ? 'Đang đăng nhập…' : <>Đăng nhập <ArrowRight size={16} /></>}
          </button>
          <p className="mt-6 flex items-center gap-1.5 text-[12px] text-chu-mo"><Lock size={12} /> Phiên được mã hoá, tự hết hạn sau 14 ngày.</p>
        </form>
      </div>
    </div>
  );
}
